import AppButton from '@nxsys/components/AppButton';

import AppDescription from '@nxsys/components/AppDescription';
import AppIconButton from '@nxsys/components/AppIconButton';
import {
  useCreateClientApplication,
  useGetClientApplication,
  useUpdateClientApplication,
} from '@nxsys/hooks/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { Row, Col, Typography } from 'antd';
import { Descriptions } from 'antd/lib';
import { useEffect, useState } from 'react';
import CreateClientApplicationModal from './components/CreateClientApplicationModal';
import { ClientApplicationValues } from './components/ClientApplicationForm';
import { IClientApplication } from '@nxsys/types/models/clientApplication';
import EditClientApplicationModal from './components/EditClientApplicationModal';
import { showMessage } from '@nxsys/utils/message';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { defaultTheme } from '@nxsys/constants/defaultConfig';

type Props = {
  data?: ICompany;
};

const ClientApplicationTab: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const { value, run } = useGetClientApplication();
  const clientApplicationData = value?.data ?? [];

  const [
    isCreateClientApplicationModalOpen,
    setIsCreateClientApplicationModalOpen,
  ] = useState(false);
  const [
    isEditClientApplicationModalOpen,
    setIsEditClientApplicationModalOpen,
  ] = useState(false);
  const [selectedClientApplication, setSelectedClientApplication] =
    useState<IClientApplication | null>(null);

  useEffect(() => {
    if (data) {
      run({ companyId: data.id, page: 0, size: 100 });
    }
  }, [data]);

  const refetch = () => {
    run({ companyId: data?.id || '', page: 0, size: 100 });
  };

  const { run: createClientApplication } = useCreateClientApplication();
  const handleCreateClientApplication = async (
    values: ClientApplicationValues,
  ) => {
    await createClientApplication({
      clientName: values.clientName,
      redirectUris: values.redirectUris,
      companyId: data?.id || '',
    });
    showMessage({
      type: 'success',
      content: messages['common.addSuccess'],
    });
    setIsCreateClientApplicationModalOpen(false);
    refetch();
  };

  const { run: updateClientApplication } = useUpdateClientApplication();
  const handleEditClientApplication = async (
    values: ClientApplicationValues,
  ) => {
    if (!selectedClientApplication?.id) return;
    await updateClientApplication(selectedClientApplication.id, {
      clientName: values.clientName,
      redirectUris: values.redirectUris,
    });
    showMessage({
      type: 'success',
      content: messages['common.updateSuccess'],
    });
    refetch();
    setIsEditClientApplicationModalOpen(false);
  };

  return (
    <>
      <div className="p-[16px]">
        <Row gutter={[24, 24]}>
          {clientApplicationData.map((item) => (
            <Col sm={24} md={12} key={item.id}>
              <div className="p-[12px] bg-neutral-50 rounded-[8px] border border-neutral-100">
                <div className="flex items-center justify-between">
                  <div className="text-primary-600">
                    {item.clientName || '-'}
                  </div>
                  <AppIconButton
                    icon={<Icons.IconEdit />}
                    title={messages['common.edit']}
                    onClick={() => {
                      setSelectedClientApplication(item);
                      setIsEditClientApplicationModalOpen(true);
                    }}
                  />
                </div>
                <div className="border-b border-neutral-100 my-[12px]" />
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item
                    label={messages['company.clientApplication.clientName']}
                  >
                    {item.clientName || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={messages['company.clientApplication.redirectUrl']}
                  >
                    {item.redirectUris.join(', ') || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={messages['common.clientId']}>
                    {item.clientId ? (
                      <Typography.Text
                        copyable
                        ellipsis={{ tooltip: item.clientId }}
                        className="!mb-0 max-w-full"
                      >
                        {item.clientId}
                      </Typography.Text>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={messages['common.clientSecret']}>
                    {item.clientSecret ? (
                      <Typography.Text
                        copyable={{ text: item.clientSecret }}
                        className="!mb-0"
                      >
                        {'••••••••••••••••'}
                      </Typography.Text>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                </AppDescription>
              </div>
            </Col>
          ))}
        </Row>
        <AppButton
          name={messages['company.clientApplication.addNew']}
          onClick={() => setIsCreateClientApplicationModalOpen(true)}
          leftIcon={<Icons.IconPlus color={defaultTheme.theme.palette.white} />}
          className="mt-[24px]"
        />
      </div>
      <CreateClientApplicationModal
        isOpen={isCreateClientApplicationModalOpen}
        toggle={() => setIsCreateClientApplicationModalOpen(false)}
        onSubmit={handleCreateClientApplication}
      />
      {isEditClientApplicationModalOpen && selectedClientApplication && (
        <EditClientApplicationModal
          isOpen={isEditClientApplicationModalOpen}
          toggle={() => setIsEditClientApplicationModalOpen(false)}
          onSubmit={handleEditClientApplication}
          data={selectedClientApplication}
        />
      )}
    </>
  );
};

export default ClientApplicationTab;
